import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import KnameBackdrop from './KnameBackdrop';
import {
  prefetchResult,
  warmSharedAssets,
  warmAssetsForMood,
} from '../../scripts/utils/kname/resultCache';

const BASE = process.env.REACT_APP_KNAME_ASSET_BASE;

const MOODS = [
  { value: '밝고 경쾌', folder: 'bright-and-cheerful', count: 3 },
  { value: '차분하고 깊은', folder: 'calm-and-profound', count: 3 },
  { value: '강하고 또렷', folder: 'strong-and-distinct', count: 3 },
  { value: '부드럽고 다정', folder: 'gentle-and-affectionate', count: 2 },
];

const MOOD_STARS = [
  { x: '10%', y: '14%', s: 3, o: 0.7 },
  { x: '88%', y: '10%', s: 2, o: 0.5 },
  { x: '95%', y: '55%', s: 2, o: 0.4 },
  { x: '6%', y: '80%', s: 2, o: 0.5 },
  { x: '48%', y: '92%', s: 2, o: 0.35 },
  { x: '70%', y: '8%', s: 2, o: 0.45 },
];

const KnameMood = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const moodImages = useMemo(
    () =>
      MOODS.map((m) => ({
        ...m,
        imageUrl: `${BASE}/v1/quiz/${m.folder}/${Math.floor(Math.random() * m.count) + 1}.jpg`,
      })),
    [],
  );

  const [picked, setPicked] = useState(null);
  const warmed = useRef(new Set());

  /**
   * 진입 즉시: 무드와 무관한 공용 에셋(폰트·명패·뒷장 베이스)을 받아둔다.
   * 어떤 무드를 고르든 반드시 쓰이므로 미리 받아도 낭비가 없다.
   */
  useEffect(() => {
    if (!state?.birthYear) return;
    warmSharedAssets();
  }, [state]);

  if (!state?.birthYear) {
    return <Navigate replace to='/korean-name-generator/form' />;
  }

  /**
   * 카드를 누르는 순간(active)에 그 무드의 에셋을 받기 시작한다.
   * onPointerDown 은 click 보다 먼저, 마우스·터치 양쪽에서 발생하므로
   * 클릭 확정 + 450ms 전환 애니메이션 동안 네트워크가 앞서 돈다.
   * 무드마다 배경·캐릭터가 달라질 수 있어 누른 것만 선택적으로 데운다. (무드당 1회)
   */
  const warmMood = (moodValue) => {
    if (warmed.current.has(moodValue)) return;
    warmed.current.add(moodValue);
    warmAssetsForMood({ ...state, mood: moodValue });
  };

  const handlePick = (mood) => {
    setPicked(mood.value);
    prefetchResult({ ...state, mood: mood.value });
    setTimeout(() => {
      navigate('/korean-name-generator/result', {
        state: { ...state, mood: mood.value },
      });
    }, 450);
  };

  return (
    <div className='relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden bg-[#17111d] px-6 py-16'>
      <KnameBackdrop stars={MOOD_STARS} glow={false} />
      <div
        aria-hidden
        className='pointer-events-none absolute left-1/2 top-1/2 h-[740px] w-[1100px] -translate-x-1/2 -translate-y-1/2'
        style={{
          background:
            'radial-gradient(ellipse, rgba(214,162,75,0.1) 0%, rgba(160,120,70,0.15) 10%, transparent 70%)',
        }}
      />

      {/* 헤드라인 */}
      <h1
        className='relative z-10 max-w-2xl text-center text-4xl leading-snug text-[#f3ede2] md:text-5xl'
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Choose the feeling your name will carry.
      </h1>
      <p className='relative z-10 mt-4 text-[16px] text-[#f2d29a]/70'>
        Trust the one that draws you in.
      </p>

      {/* 무드 카드 4장 */}
      <div className='relative z-10 mt-12 grid w-full max-w-6xl grid-cols-2 gap-4 lg:grid-cols-4'>
        {moodImages.map((mood) => (
          <button
            key={mood.value}
            type='button'
            onClick={() => handlePick(mood)}
            onPointerDown={() => warmMood(mood.value)}
            className={`group overflow-hidden rounded-2xl border bg-[#221820] p-2 pb-3 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_16px_40px_-16px_rgba(214,162,75,0.4)] active:scale-95 ${
              picked === mood.value
                ? 'border-[#d9b380] -translate-y-4 scale-105 shadow-[0_24px_50px_-16px_rgba(214,162,75,0.5)]'
                : picked
                  ? 'border-[#3a2f3d] opacity-40'
                  : 'border-[#3a2f3d]'
            }`}
          >
            <div className='aspect-[3/4] w-full overflow-hidden rounded-xl bg-[#171019]'>
              <img
                src={mood.imageUrl}
                alt=''
                className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                draggable={false}
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default KnameMood;
